import { Injectable, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ResultsView } from '@clubhub/ignite/models/affordability/enums/results-view';
import { StepName } from '@clubhub/ignite/models/affordability/step';
import { CriteriaSteps } from '@clubhub/ignite/models/criteria/criteria-steps';
import { OutcomeHistoryResponse } from '@clubhub/ignite/models/outcome-history';
import { BasketService } from '@clubhub/services';
import { isUsableNumber } from '@shared/helpers';
import { MiProductsFiltersViewModel } from '@shared/models';
import { LendingTypeCode, SearchType } from '@shared/models/affordability-enums';
import { LenderAuditType } from '@shared/models/lender-audit';
import { OutcomeDetails } from '@shared/models/outcome';
import { AuthorizeService } from '@shared/services/authorize.service';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { LendingTypeService } from '@shared/services/lending-type.service';
import { of } from 'rxjs/internal/observable/of';
import { AffordabilityService } from './affordability/affordability.service';
import { RioService } from './affordability/rio.service';
import { CriteriaNavigateService } from './criteria/criteria-navigate.service';
import { CriteriaV2Service } from './criteria/criteria-v2.service';
import { IgniteService } from './ignite.service';
import { IgniteCommonDataService } from './ignite.store';
import { ProductsSearchService } from './products/products-search.service';
import { PropertySearchService } from './property/property-search.service';

@Injectable({
  providedIn: 'root',
})
export class IgniteHistoryService {
  private readonly clubHubDataService = inject(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly basketService = inject(BasketService);
  private readonly criteriaV2Service = inject(CriteriaV2Service);
  private readonly router = inject(Router);
  private readonly rioService = inject(RioService);
  private readonly productsSearchService = inject(ProductsSearchService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);
  private readonly igniteService = inject(IgniteService);
  private readonly criteriaNavigateService = inject(CriteriaNavigateService);
  private readonly propertySearchService = inject(PropertySearchService);
  private readonly affordabilityService = inject(AffordabilityService);

  public historyCreatedDate: Date | undefined;

  public getHistoricOutcome(
    outcomeSearchId: string,
    isInaccuracy: boolean,
    criteriaV2: boolean,
    savedReport: boolean,
    isSsoUser: boolean,
    agencyNumber?: number,
    frnNumber?: number,
    key?: string,
    code?: number,
  ) {
    let outcomeUrl = `outcomehistory/${outcomeSearchId}?inaccuracy=${isInaccuracy}&isSsoUser=${isSsoUser}`;
    // these must be null/NaN checks rather than truthiness checks: agency number 0 is a valid internal agency
    // and dropping it from the url makes the api reject the request as unauthorized (MCONE - criteria email link)
    if (isUsableNumber(agencyNumber)) {
      outcomeUrl += `&agencyNumber=${agencyNumber}`;
    }
    if (isUsableNumber(frnNumber)) {
      outcomeUrl += `&frnNumber=${frnNumber}`;
    }
    // this token key is needed to authenticate the user on the endpoint if they're not logged in
    if (key) {
      outcomeUrl += `&key=${key}`;
    }
    if (isUsableNumber(code)) {
      outcomeUrl += `&code=${code}`;
    }
    this.authService.historyView = true;
    this.productsSearchService.startLoadOutcomeHistory();
    this.clubHubDataService.get<OutcomeHistoryResponse>(outcomeUrl).subscribe({
      next: result => {
        this.processHistoricOutcome(result, isInaccuracy, savedReport, false);
        setTimeout(() => {
          this.productsSearchService.stopLoadOutcomeHistory();
        }, 300);
      },
      // without this the loading counter is never decremented, leaving a permanent spinner behind the error
      error: () => this.productsSearchService.stopLoadOutcomeHistory(),
    });
  }

  public getHistoricAuditOutcome(referenceId: string, auditType: LenderAuditType) {
    this.productsSearchService.startLoadOutcomeHistory();
    this.clubHubDataService.get<OutcomeHistoryResponse>(`outcomehistory/audit/${referenceId}?auditType=${auditType}`).subscribe(result => {
      this.processHistoricOutcome(result, false, false, true);
      setTimeout(() => {
        this.productsSearchService.stopLoadOutcomeHistory();
      }, 300);
    });
  }

  public productsFilters: MiProductsFiltersViewModel[] = [];

  private reset() {
    this.productsFilters = [];
  }

  private processHistoricOutcome(result: OutcomeHistoryResponse, isInaccuracy: boolean, savedReport: boolean, audit: boolean) {
    this.reset();
    this.igniteService.resetResults();
    this.productsSearchService.resetProducts();
    this.productsSearchService.customProductCalculations = result.customProductSettingsViewModel;
    this.igniteCommonDataService.source = result.source;
    this.igniteService.addToSearchInUse(result);
    this.lendingTypeService.setLendingTypeByName(result.lendingType);
    this.igniteService.form.markAsUntouched();
    this.affordabilityService.affordabilityModelRequest = null;
    this.igniteService.resetOptions();
    this.igniteService.model = this.igniteService.initialModel();
    this.rioService.initializeRioForm();
    this.igniteService.form = new UntypedFormGroup({});
    this.igniteService.options = {};
    this.igniteService.savedReport = savedReport;
    this.historyCreatedDate = result.createdDate;

    if (isInaccuracy) {
      this.igniteCommonDataService.isInaccuracyReport = true;
    } else {
      this.igniteCommonDataService.isOutcomeHistory = true;
    }

    if (result.affordability) {
      if (result.affordability.uiModelJson) {
        this.igniteService.model = JSON.parse(result.affordability.uiModelJson);
        this.igniteService.model?.applicants?.applicantAges?.forEach((applicant, i) => {
          applicant.applicantNumber = i + 1;
        });
        this.igniteService.model?.incomeAndExpenditure?.applicants?.forEach((applicant, i) => {
          applicant.applicantNumber = i + 1;
        });
        this.affordabilityService.affordabilityModelRequest = this.igniteService.mappedModel(this.igniteService.model);
        if (this.igniteService.lendingTypeCode === LendingTypeCode.Res) {
          this.rioService.reset(this.igniteService.model?.applicants?.numberOfApplicants, this.igniteService.model);
        }
        this.igniteService.form.reset(this.igniteService.model);
        this.igniteCommonDataService.affordabilityModel = this.igniteService.model ?? null;
      }

      this.igniteCommonDataService.results = {
        pollJobID: null,
        quotes: result.affordability.affordabilityResults,
        affordabilitySearchId: result.affordability.affordabilityResults[0]?.affordabilitySearchId ?? null,
        errorMessage: null,
        completed: true,
        lenderCount: result.affordability.affordabilityResults.length,
      };

      this.igniteCommonDataService.lenders = result.affordability.affordabilityResults.map(r => ({
        lenderName: r.lenderName,
        lenderLogoUrl: r.lenderLogoUrl ?? '',
      }));

      this.igniteService.miSaveOutcomeModel = result.affordability.saveOutcomeModel;
    }

    this.basketService.clearBasket();

    if (result.criteria) {
      this.criteriaV2Service.criteriaSearchId = result.criteria.criteriaSearchId;
      this.setCriteria(result.criteria);
      this.criteriaV2Service.additionalOutcomes = result.criteria.details;
    }

    if (result.intuitiveOutcomes) {
      this.setCriteria(result.intuitiveOutcomes);
      this.criteriaV2Service.intuitiveOutcomes = result.intuitiveOutcomes.details;
    }

    if (result.property) {
      this.propertySearchService.propertySearchId = result.property.criteriaSearchId;
      this.setCriteria(result.property);
      this.propertySearchService.propertyOutcomes = result.property.details;
      this.propertySearchService.autoAddedPropertyOutcomeIds = result.property.autoAddedOutcomeIds ?? [];
    }

    if (result.products) {
      this.productsFilters = result.products.miProductsFilters;
      this.productsSearchService.processHistoricData(result.products);
      if (audit) {
        this.loadHistoricProducts();
      }
      this.igniteService.matchedLenderProducts = this.productsSearchService.matchedProducts ?? [];
    }

    if (result.source === SearchType.Affordability) {
      this.igniteService.currentStepName = StepName.Results;
      this.igniteCommonDataService.resultsView = isInaccuracy ? ResultsView.Input : ResultsView.Details;
    }

    if (result.source === SearchType.Criteria || result.source === SearchType.Property) {
      this.criteriaV2Service.step = CriteriaSteps.CriteriaResult;
      this.igniteService.currentStepName = StepName.Results;
      this.igniteCommonDataService.resultsView = ResultsView.Summary;
    }

    if (result.source === SearchType.Product) {
      this.igniteService.currentStepName = StepName.Results;
      this.igniteCommonDataService.resultsView = ResultsView.MatchedProducts;
    }

    this.propertySearchService.splitOutPropertyCriteria(result.intuitiveOutcomes?.details ?? null, result.criteria?.details ?? []);

    const lendingType = this.lendingTypeService.lendingType?.code ?? '';

    // navigate to place tool where the search was saved - property searches will land on the criteria page
    // as they share the same results page
    if ((!!result.property || !!result.criteria) && !result.affordability && !result.products) {
      this.criteriaNavigateService.navigate('issues', { queryParams: { historicResult: true, audit } });
    } else if (!!result.products && !result.affordability) {
      this.router.navigateByUrl(`ignite/products/form/${lendingType}?historicResult=true&audit=${audit}`);
    } else {
      this.router.navigateByUrl(`ignite/affordability/form/${lendingType}?historicResult=true&audit=${audit}`);
    }
  }

  private setCriteria(criteria: OutcomeDetails) {
    // merge into any existing outcome (criteria/intuitiveOutcomes/property can all be present on the
    // same history result) instead of overwriting it, otherwise whichever of the three is processed
    // last would wipe out the details from the others. mergeHistoricOutcomeDetails keeps every existing
    // contextResult (including auto-added ones) and only replaces the specific contexts the incoming
    // result also contains, rather than only merging Intuitive-type contexts (see mergeOutcomeDetails).
    if (this.criteriaV2Service.outcome?.details?.length) {
      this.criteriaV2Service.mergeHistoricOutcomeDetails(criteria);
      this.criteriaV2Service.outcome.contexts = [...(this.criteriaV2Service.outcome.contexts ?? []), ...(criteria.contexts ?? [])];
    } else {
      this.criteriaV2Service.outcome = criteria;
    }
    this.criteriaV2Service.setBasketItems(criteria.contexts, false, criteria.autoAddedOutcomeIds ?? []);
    this.criteriaV2Service.step = CriteriaSteps.CriteriaResult;

    this.igniteService.joinCriteriaResults(criteria.details);
  }

  public loadHistoricProducts() {
    if (!this.productsSearchService.productsSearchId) {
      return of();
    }
    this.productsSearchService.loading = true;

    return this.productsSearchService
      .getPreviousOutcome(this.productsSearchService.productsSearchId, false, this.igniteCommonDataService.outcomehistoryId)
      .subscribe(result => {
        this.productsFilters = result.miProductsFilters;

        this.productsSearchService.processHistoricProducts(result);
        if (this.igniteCommonDataService.affordabilitySearchId) {
          this.igniteService.matchedLenderProducts = this.productsSearchService.matchedProducts ?? [];
          if (this.igniteCommonDataService.results) {
            this.productsSearchService.matchedProductsFlat = this.productsSearchService.getLendersProductsFlat(
              this.igniteCommonDataService.results,
            );
          }
        }
      });
  }
}
